import { useEffect,useState } from 'react';
import { useDispatch } from 'react-redux';

import { useGetCurrentParticipantQuery } from 'store/api/pkd/endpoints/user';
import {
  useFetch1UpProvidersListQuery,
  useResource1UpDataFetchStatusQuery
} from 'store/api/pkd/endpoints/oneUp';
import { useGetAllProvidersQuery } from 'store/api/pkd/endpoints/oneRecord';
import { openSnackbar } from 'store/application';

export const PROVIDER_VENDOR = {
  ONE_RECORD : 'OneRecord',
  ONE_UP     : '1up Health',
};

export const PROVIDER_STATUS = {
  INITIATED          : 'initiated',
  SUCCESS            : 'success',
  ERROR              : 'error',
  VALIDATION_SUCCESS : 'validation_success',
  VALIDATION_FAILED  : 'validation_failed',
  CANCELLED          : 'cancelled',
};

const TYPE_ELECTRONIC = 'electronic';
const TWENTY_MINUTES = 20 * 60 * 1000;

export const transformOneUpProvider = (providerId, details) => {
  const detail = details?.filter((d) => d?.id === parseInt(providerId))?.[0];
  const address = detail?.locations?.[0]?.address;

  return {
    name           : detail?.name,
    zip            : address?.postalCode,
    city           : address?.city,
    state          : address?.state,
    street         : address?.line[0],
    providerVendor : PROVIDER_VENDOR.ONE_UP,
  };
};

export const transformProviderInfo = (originalProviderInfo) => {
  if (!originalProviderInfo?.data?.length) {
    return {};
  }

  // Extract the last item from the data array
  const latestItem =
    originalProviderInfo.data[originalProviderInfo.data.length - 1];

  // Create a new object with the added_after_reopen property and all properties from the latest item
  const transformedProviderInfo = {
    providerVendor : PROVIDER_VENDOR.ONE_RECORD,
    ...latestItem,
  };
  return transformedProviderInfo;
};

export const useListConnectedCareCenters = () => {
  const dispatch = useDispatch();
  const [hasInProgressCenters, setHasInProgressCenters] = useState(false);
  const { data: participant, isLoading: isParticipantLoading } = useGetCurrentParticipantQuery();
  const participantId = participant?.id;

  
  const {
    connectedCareCenters: rawConnectedCareCenters = [],
    isError: isFetchStatusError,
    isLoading: isStatusLoading,
    oneUpfilteredProviders = [],
  } = useGetAllProvidersQuery(
    { participantId, getAllProvider: true },
    {
      skip             : !participantId,
      pollingInterval  : hasInProgressCenters ? TWENTY_MINUTES : 0,
      selectFromResult : ({ data, isLoading }) => {
        let connectedCareCenters = undefined;
        let oneUpfilteredProviders = [];
        if (data) {
          oneUpfilteredProviders = data?.data?.filter(
            (p) =>
              p?.attributes?.type === TYPE_ELECTRONIC &&
              p?.attributes?.interopVendor === PROVIDER_VENDOR.ONE_UP,
          );
          const filteredProviders = data?.data?.filter(
            (p) =>
              p?.attributes?.type === TYPE_ELECTRONIC &&
              p?.attributes?.status !== PROVIDER_STATUS.CANCELLED &&
              p?.attributes?.interopVendor === PROVIDER_VENDOR.ONE_RECORD,
          );
          let result = [
            ...((filteredProviders || []).map((c) => ({
              ...c,
              ...transformProviderInfo(c?.attributes?.providerInfo),
            }))),
            ...(oneUpfilteredProviders || []),
          ];
          result = result.filter((r) => r !== null && r !== undefined);
          connectedCareCenters = result;
        }
        return {
          connectedCareCenters,
          isLoading,
          oneUpfilteredProviders,
        };
      },
    }
  );
  let oneUpSuccessIds = [];
  const shouldCallResourceStatus = oneUpfilteredProviders.length > 0 && participantId;
  const { data: resourceStatusData = {}, isLoading: isResourceStatusLoading } = useResource1UpDataFetchStatusQuery(
    { participantId },
    { skip: !shouldCallResourceStatus }
  );
  if (shouldCallResourceStatus && resourceStatusData) {
    oneUpSuccessIds = Object.entries(resourceStatusData)
      .filter(([_, val]) => val.status === PROVIDER_STATUS.SUCCESS)
      .map(([id]) => id);
  }
  const formattedIds = oneUpSuccessIds.length > 0 ? oneUpSuccessIds.join(',') : '';
  const shouldSkip = !formattedIds;
  const { data: details = [], isLoading: isDetailsLoading } = useFetch1UpProvidersListQuery(
    { id: formattedIds },
    { skip: shouldSkip }
  );
  // Transform OneUp providers with fetched details
  const connectedCareCenters = rawConnectedCareCenters
    .map((provider) => {
      // Only include OneUp providers whose healthSystemId matches an id in details
      if (
        provider?.attributes?.interopVendor === PROVIDER_VENDOR.ONE_UP &&
        details.length > 0 &&
        details.some((d) => String(d.id) === String(provider?.attributes?.healthSystemId))
      ) {
        const transformed = {
          ...provider,
          ...transformOneUpProvider(
            provider.attributes?.healthSystemId,
            details,
          ),
        };
        // If status is null, set it to 'success' as details were fetched after checking the status from old oneUp API call.
        if (provider?.attributes?.status == null) {
          transformed.attributes = {
            ...provider.attributes,
            status : PROVIDER_STATUS.SUCCESS,
          };
        }
        return transformed;
      }
      // Exclude OneUp providers that do not meet the criteria
      if (provider?.attributes?.interopVendor === PROVIDER_VENDOR.ONE_UP) {
        return null;
      }
      // Include all other providers (OneRecord, etc.)
      return provider;
    })
    .filter((p) => p !== null);

  const isLoading = isStatusLoading || isParticipantLoading || isDetailsLoading || isResourceStatusLoading;
  const hasInProgress = Object.values(connectedCareCenters).some(
    (center) => center?.attributes?.status === PROVIDER_STATUS.INITIATED
  );
  useEffect(() => {
    setHasInProgressCenters(hasInProgress);
  }, [hasInProgress]);
  useEffect(() => {
    if (isFetchStatusError) {
      dispatch(
        openSnackbar({
          type    : 'error',
          message : 'Sorry an unexpected error occured. Try again alter.',
        })
      );
    }
  }, [isFetchStatusError, dispatch]);

  return { connectedCareCenters, isLoading };
};
